import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../styles/CommonElements';
import logoImage from '../assets/logo.png';
import { useModalContext } from '../App';

const HeaderContainer = styled.header`
  padding: 1.5rem 0;
  background-color: white;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    padding: 1.8rem 0;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    position: relative;
    justify-content: flex-start;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 60px;
    width: auto;
  }
  
  @media (max-width: 768px) {
    position: relative;
    left: 0;
    transform: none;
    margin-left: 20px;
  }
`;

const HamburgerMenu = styled.div`
  display: none;
  cursor: pointer;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 30px;
    height: 21px;
    position: absolute;
    right: 20px;
    z-index: 10;
  }
  
  span {
    display: block;
    height: 3px;
    width: 100%;
    background-color: #333;
    border-radius: 3px;
    transition: all 0.3s ease;
  }
  
  &.open {
    span:nth-child(1) {
      transform: translateY(9px) rotate(45deg);
    }
    
    span:nth-child(2) {
      opacity: 0;
    }
    
    span:nth-child(3) {
      transform: translateY(-9px) rotate(-45deg);
    }
  }
`;

const MobileNav = styled.nav<{ $isOpen: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: white;
    padding: 80px 20px 40px;
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
    transition: transform 0.3s ease;
    z-index: 5;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    text-align: center;
  }
  
  li {
    margin: 2rem 0;
    
    span {
      display: inline-block;
      font-size: 1.4rem;
      font-weight: 500;
      color: #333;
      cursor: pointer;
      position: relative;
      padding: 8px 0;
    }
    
    a {
      font-size: 1.3rem;
      font-weight: 500;
      color: #333;
      text-decoration: none;
    }
  }
  
  .mobile-dropdown {
    background-color: #f8f8f8;
    border-radius: 8px;
    margin: 1rem auto;
    padding: 0.5rem 0;
    max-width: 90%;
    
    li {
      margin: 1rem 0;
      
      a {
        font-size: 1.1rem;
        color: #555;
        display: block;
        padding: 6px 0;
      }
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NavList = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  justify-content: center;
`;

const NavItem = styled.li<{ $isActive: boolean }>`
  margin: 0 1.25rem;
  
  a {
    position: relative;
    font-weight: 500;
    color: ${props => props.$isActive ? 'var(--primary-color)' : 'inherit'};
    padding: 0.5rem 0;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -5px;
      left: 0;
      width: ${props => props.$isActive ? '100%' : '0'};
      height: 2px;
      background-color: var(--primary-color);
      transition: width 0.3s ease;
    }
    
    &:hover:after {
      width: 100%;
    }
  }
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: relative;
  
  span {
    cursor: pointer;
    display: flex;
    align-items: center;
    font-weight: 500;
    
    svg {
      margin-left: 5px;
      transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
      transition: transform 0.3s ease;
    }
  }
`;

const DropdownContent = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  background: white;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem 0;
  min-width: 240px;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.$isOpen ? 'translateY(10px)' : 'translateY(0)'};
  transition: all 0.3s ease;
  z-index: 10;
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  li {
    padding: 0.7rem 1.5rem;
    
    &:hover {
      background-color: #f8f8f8;
    }
    
    a {
      color: #333;
      display: block;
      font-size: 1.05rem;
      white-space: nowrap;
      
      &:after {
        display: none;
      }
    }
  }
`;

const HeaderButton = styled(Button)`
  padding: 0.75rem 1.75rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 6px;
  transition: all 0.3s ease;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 10px rgba(255, 107, 0, 0.2);
  }
  
  svg {
    margin-left: 2px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(3px);
  }
`;

const MobileNavExtended = styled(MobileNav)`
  .mobile-button {
    margin: 2rem auto 0;
    width: 80%;
    max-width: 250px;
    justify-content: center;
    font-size: 1.1rem;
    padding: 1rem 1.75rem;
  }
`;

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const solutionsRef = useRef<HTMLDivElement>(null);
  const { openLearnMoreModal } = useModalContext();
  
  // Close menus when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setSolutionsOpen(false);
    setMobileSolutionsOpen(false);
  }, [location.pathname]);
  
  // Close the desktop dropdown on outside click or Escape
  useEffect(() => {
    if (!solutionsOpen) return;
    
    const handleOutside = (e: MouseEvent) => {
      if (solutionsRef.current && !solutionsRef.current.contains(e.target as Node)) {
        setSolutionsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSolutionsOpen(false);
    };
    
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [solutionsOpen]);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  const handleStartProject = () => {
    openLearnMoreModal('Custom Project');
  };
  
  // Custom smooth scroll function
  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    
    // Check if we need to navigate to another page
    const targetPath = e.currentTarget.pathname;
    const isNavigatingToAnotherPage = targetPath !== '/' && location.pathname !== targetPath;
    
    if (isNavigatingToAnotherPage) {
      // Navigate to another page with hash
      window.location.href = `${targetPath}#${id}`;
      return;
    }
    
    // Find the element to scroll to
    const element = document.getElementById(id);
    
    if (element) {
      // Get header height for offset
      const headerHeight = document.querySelector('header')?.offsetHeight || 100;
      
      // Calculate the target scroll position
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
      
      // Smooth scroll to the target position
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Update URL hash after scrolling
      window.history.pushState(null, '', `${location.pathname}#${id}`);
    } else if (location.pathname !== '/') {
      // If element doesn't exist and we're not on homepage, navigate there first
      window.location.href = `/#${id}`;
    }
  };
  
  return (
    <HeaderContainer>
      <div className="container font--jakarta">
        <HeaderContent>
          <Logo>
            <Link to="/">
              <img src={logoImage} alt="Logo" />
            </Link>
          </Logo>
          
          <Nav>
            <NavList>
              <NavItem $isActive={location.pathname.startsWith('/solutions')}>
                <DropdownMenu $isOpen={solutionsOpen} ref={solutionsRef}>
                  <span
                    onClick={() => setSolutionsOpen(!solutionsOpen)}
                    aria-haspopup="true"
                    aria-expanded={solutionsOpen}
                  >
                    Solutions
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <DropdownContent $isOpen={solutionsOpen}>
                    <ul>
                      <li><Link to="/solutions/business-portals">Business Portals</Link></li>
                      <li><Link to="/solutions/system-integrations">System Integrations</Link></li>
                      <li><Link to="/solutions/workflow-automation">Workflow Automation</Link></li>
                      <li><Link to="/#process">Our Process</Link></li>
                    </ul>
                  </DropdownContent>
                </DropdownMenu>
              </NavItem>
              <NavItem $isActive={location.pathname.includes('/process')}>
                <a href="/#process" onClick={(e) => handleSmoothScroll(e, 'process')}>Process</a>
              </NavItem>
              <NavItem $isActive={location.pathname === '/about'}>
                <Link to="/about">About</Link>
              </NavItem>
              <NavItem $isActive={location.pathname === '/contact'}>
                <Link to="/contact">Contact</Link>
              </NavItem>
            </NavList>
          </Nav>
          
          <ActionsContainer>
            <HeaderButton onClick={handleStartProject}>
              Get Started
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </HeaderButton>
          </ActionsContainer>
          
          <HamburgerMenu className={mobileMenuOpen ? 'open' : ''} onClick={toggleMobileMenu}>
            <span></span>
            <span></span>
            <span></span>
          </HamburgerMenu>
          
          <MobileNav $isOpen={mobileMenuOpen}>
            <ul>
              <li>
                <span onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}>
                  Solutions
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ marginLeft: 6, verticalAlign: 'middle', transform: mobileSolutionsOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                  >
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                {mobileSolutionsOpen && (
                  <ul className="mobile-dropdown">
                    <li><Link to="/solutions/business-portals">Business Portals</Link></li>
                    <li><Link to="/solutions/system-integrations">System Integrations</Link></li>
                    <li><Link to="/solutions/workflow-automation">Workflow Automation</Link></li>
                    <li><Link to="/#process">Our Process</Link></li>
                  </ul>
                )}
              </li>
              <li>
                <a href="/#process" onClick={(e) => {handleSmoothScroll(e, 'process'); toggleMobileMenu();}}>Process</a>
              </li>
              <li>
                <Link to="/about" onClick={toggleMobileMenu}>About</Link>
              </li>
              <li>
                <Link to="/contact" onClick={toggleMobileMenu}>Contact</Link>
              </li>
              <li>
                <HeaderButton 
                  onClick={() => {handleStartProject(); toggleMobileMenu();}}
                  style={{
                    margin: '2rem auto 0',
                    width: '80%',
                    maxWidth: '250px', 
                    justifyContent: 'center',
                    fontSize: '1.1rem',
                    padding: '1rem 1.75rem'
                  }}
                >
                  Get Started
                </HeaderButton>
              </li>
            </ul>
          </MobileNav>
        </HeaderContent>
      </div>
    </HeaderContainer>
  );
};

export default Header;